import {SemanticLegendSettingsWithCategoricalConfig} from '../../components/WorkspaceDrawer/Settings/types';
import * as ColorUtil from '../../util/colors';
import {ColorPaletteOption} from '../../util/colors';
import {RunColorConfig} from '../../util/section';
import {LineResult} from './lineResultsForRunset';
import {Line, Mark} from './types';

export function getLinesColoredByMetric(lineResults: LineResult[]): Line[][][] {
  let lineColorIdx = 0;
  return lineResults.flatMap(lr => {
    return lr.linesByKeyAndMetricName.map(linesGroupByMetric => {
      return linesGroupByMetric.map(lineGroup => {
        const newLineGroup = lineGroup.map(l => {
          const newLine = {
            ...l,
            color: ColorUtil.color(lineColorIdx, l.aux ? 0.1 : undefined),
          } as Line;

          return newLine;
        });
        lineColorIdx += 1;
        return newLineGroup;
      });
    });
  });
}

export function getLinesColoredByRun(
  lineResults: LineResult[],
  colorEachMetricDifferently: boolean,
  colorPaletteOption: ColorPaletteOption,
  customRunColors?: RunColorConfig,
  semanticLegendSettings?: SemanticLegendSettingsWithCategoricalConfig
): Line[][][] {
  let lineColorIdx = 0;
  return lineResults.flatMap(lr => {
    if (
      lr.groupingType === 'Panel' ||
      customRunColors == null ||
      colorEachMetricDifferently
    ) {
      // If we're doing panel grouping OR old-stype coloring, apply round-robin colors.
      // There's extra nesting here so that we can capture mean & area lines together.
      const coloredLinesByKeyAndMetricName = lr.linesByKeyAndMetricName.map(
        linesGroupByMetric => {
          const coloredLineGroupByMetric: Line[][] = linesGroupByMetric.map(
            lineGroup => {
              const coloredLineGroup = lineGroup.map(l => {
                const color = ColorUtil.color(
                  lineColorIdx,
                  l.aux ? 0.1 : undefined
                );

                const newLine = {
                  ...l,
                  color,
                } as Line;
                return newLine;
              });
              if (colorEachMetricDifferently) {
                lineColorIdx++;
              }
              return coloredLineGroup;
            }
          );
          if (!colorEachMetricDifferently) {
            lineColorIdx++;
          }
          return coloredLineGroupByMetric;
        }
      );
      return coloredLinesByKeyAndMetricName;
    } else {
      // Otherwise, we're doing new coloring either grouped or ungrouped. In either case,
      // it's the same codepath.
      const colLines = lr.linesByKeyAndMetricName.map(linesGroupByMetric => {
        const cl = linesGroupByMetric.map(lineGroup => {
          const newLineGroup = lineGroup.map(l => {
            const color = ColorUtil.runColor({
              run: l.run!,
              groupKeys: lr.groupKeys,
              customRunColors,
              semanticLegendSettings,
              alpha: l.aux ? 0.1 : undefined,
              colorPaletteOption,
            });

            const newLine = {
              ...l,
              color,
            } as Line;
            return newLine;
          });

          return newLineGroup;
        });
        lineColorIdx++;
        return cl;
      });
      return colLines;
    }
  });
}

// warning: this mutates the lines
export function markLinesByColor(
  lines: Array<{
    color?: string;
    mark?: string;
    run?: {isBaseline?: boolean; isPinned?: boolean};
  }>
) {
  const lineCountByColor = new Map<string, number>();

  const lineMarkOptions: Mark[] = [
    'solid',
    'dashed',
    'dotted',
    'dotdash',
    'dotdotdash',
  ];

  lines.forEach(l => {
    if (!l || !l.color) {
      return;
    } // skip if no color

    // Skip baseline runs - they should always stay solid
    if (l.run?.isBaseline) {
      l.mark = 'solid';
      return;
    }

    // Find the number of previous lines that are the same color as this line
    const sameColorCount = lineCountByColor.get(l.color) || 0;

    // Round-robin assignment of mark options
    l.mark = lineMarkOptions[sameColorCount % lineMarkOptions.length];

    lineCountByColor.set(l.color, sameColorCount + 1);
  });
}

/**
 * Applies baseline run line styling based on hover state.
 * - When NOT hovered: baseline run is solid (default)
 * - When hovered: baseline run becomes dashed to distinguish from hovered run
 *
 * @param lines - Array of lines to style (mutated in place)
 * @param isHovered - Whether any chart is currently being hovered
 * @returns The same array with baseline line styling applied (mutated in place)
 */
export function applyBaselineLineStyles(
  lines: Line[],
  isHovered: boolean
): Line[] {
  // Mutate baseline lines in place for performance
  for (const line of lines) {
    if (line.run?.isBaseline) {
      line.mark = isHovered ? 'dashed' : 'solid';
    }
  }
  return lines;
}
